import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { Heart, ShoppingBag, Share2, BookOpen, Globe, Layers, Star, X } from "lucide-react";
import { toast } from "sonner";
import BookCard from "@/components/BookCard";
import { Dialog, DialogContent } from "@/components/ui/dialog";

export default function BookDetail() {
  const { id } = useParams();
  const { add, toggleWishlist, isInWishlist } = useCart();
  const { user } = useAuth();
  const [book, setBook] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [similar, setSimilar] = useState([]);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    api.get(`/books/${id}`).then((r) => setBook(r.data));
    api.get(`/books/${id}/reviews`).then((r) => setReviews(r.data));
  }, [id]);

  useEffect(() => {
    if (!book) return;
    api.get("/books", { params: { category: book.category, limit: 4 } })
      .then((r) => setSimilar(r.data.items.filter((b) => b.id !== book.id).slice(0, 3)));
  }, [book]);

  const buyNow = async () => {
    const ok = await add(id);
    if (ok) window.location.href = "/panier";
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ url, title: book.title }); return; } catch {} }
    navigator.clipboard.writeText(url);
    toast.success("Lien copié !");
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Connectez-vous pour laisser un avis"); return; }
    await api.post(`/books/${id}/reviews`, { book_id: id, rating, comment });
    setComment(""); setRating(5);
    toast.success("Merci pour votre avis !");
    const { data } = await api.get(`/books/${id}/reviews`);
    setReviews(data);
  };

  if (!book) return <div className="max-w-7xl mx-auto px-6 py-24 text-center text-muted-foreground">Chargement...</div>;

  const promo = book.original_price && book.original_price > book.price;
  const avgRating = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 fade-in">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="sticky top-24">
            <div className="aspect-[2/3] overflow-hidden border border-border book-shadow">
              <img src={book.cover_url} alt={book.title} className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="label-eyebrow text-accent mb-3">{book.category}</div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">{book.title}</h1>
          <div className="mt-3 text-muted-foreground">par <span className="text-foreground">{book.author}</span></div>

          {reviews.length > 0 && (
            <div className="mt-4 flex items-center gap-1 text-sm">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-4 h-4 ${i < Math.round(avgRating) ? "fill-accent text-accent" : "text-muted-foreground"}`} />
              ))}
              <span className="text-muted-foreground ml-2">({reviews.length})</span>
            </div>
          )}

          <div className="mt-6 flex items-baseline gap-4">
            <div className="font-serif text-4xl">{book.price.toFixed(2).replace(".", ",")} €</div>
            {promo && <div className="text-muted-foreground line-through text-lg">{book.original_price.toFixed(2).replace(".", ",")} €</div>}
          </div>

          <p className="mt-8 leading-relaxed text-foreground/90">{book.description}</p>

          <div className="mt-8 grid grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-2"><Layers className="w-4 h-4 text-accent" /> {book.pages} pages</div>
            <div className="flex items-center gap-2"><Globe className="w-4 h-4 text-accent" /> {book.language}</div>
            <div className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-accent" /> Format PDF</div>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Button data-testid="detail-add-cart" onClick={() => add(id)} size="lg" variant="outline" className="rounded-sm h-12">
              <ShoppingBag className="w-4 h-4 mr-2" /> Ajouter au panier
            </Button>
            <Button data-testid="detail-buy-now" onClick={buyNow} size="lg" className="rounded-sm h-12">Acheter maintenant</Button>
            {book.flipbook_preview_url && (
              <Button data-testid="detail-preview" onClick={() => setPreviewOpen(true)} size="lg" variant="outline" className="rounded-sm h-12">
                <BookOpen className="w-4 h-4 mr-2" /> Feuilleter un extrait
              </Button>
            )}
            <button
              data-testid="detail-wishlist"
              onClick={() => toggleWishlist(id)}
              className="w-12 h-12 flex items-center justify-center border border-border hover:border-accent transition-colors"
              aria-label="Favori"
            >
              <Heart className={`w-4 h-4 ${isInWishlist(id) ? "fill-accent text-accent" : ""}`} />
            </button>
            <button
              data-testid="detail-share"
              onClick={share}
              className="w-12 h-12 flex items-center justify-center border border-border hover:border-accent transition-colors"
              aria-label="Partager"
            >
              <Share2 className="w-4 h-4" />
            </button>
          </div>

          {book.toc && book.toc.length > 0 && (
            <div className="mt-16">
              <div className="label-eyebrow text-accent mb-4">Table des matières</div>
              <ol className="space-y-2 text-sm">
                {book.toc.map((t, i) => (
                  <li key={i} className="py-3 border-b border-border flex gap-4">
                    <span className="text-muted-foreground w-8">{String(i + 1).padStart(2, "0")}</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Reviews */}
          <div className="mt-16">
            <div className="label-eyebrow text-accent mb-4">Avis lecteurs</div>
            {reviews.length === 0 ? (
              <p className="text-muted-foreground text-sm">Aucun avis pour l'instant.</p>
            ) : (
              <div className="space-y-6">
                {reviews.map((r) => (
                  <div key={r.id} className="pb-6 border-b border-border">
                    <div className="flex items-center gap-3">
                      <div className="font-medium">{r.user_name}</div>
                      <div className="flex">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} className={`w-3 h-3 ${i < r.rating ? "fill-accent text-accent" : "text-muted-foreground"}`} />
                        ))}
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-foreground/80">{r.comment}</p>
                  </div>
                ))}
              </div>
            )}

            {user && (
              <form onSubmit={submitReview} className="mt-8 p-6 border border-border">
                <div className="label-eyebrow mb-3">Laisser un avis</div>
                <div className="flex gap-1 mb-3">
                  {[1,2,3,4,5].map((n) => (
                    <button type="button" key={n} onClick={() => setRating(n)} data-testid={`review-star-${n}`}>
                      <Star className={`w-5 h-5 ${n <= rating ? "fill-accent text-accent" : "text-muted-foreground"}`} />
                    </button>
                  ))}
                </div>
                <textarea
                  data-testid="review-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  required
                  className="w-full min-h-24 bg-transparent border border-border p-3 text-sm rounded-sm"
                  placeholder="Votre ressenti..."
                />
                <Button type="submit" data-testid="review-submit" className="mt-3 rounded-sm">Publier</Button>
              </form>
            )}
          </div>
        </div>
      </div>

      {similar.length > 0 && (
        <div className="mt-24">
          <div className="label-eyebrow text-accent mb-3">Livres similaires</div>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-12">Vous pourriez aussi aimer</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {similar.map((b) => <BookCard key={b.id} book={b} />)}
          </div>
        </div>
      )}

      {/* Flipbook preview modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-6xl w-[95vw] p-0 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-border">
            <div>
              <div className="label-eyebrow text-accent">Extrait</div>
              <div className="font-serif text-lg leading-tight">{book?.title}</div>
            </div>
            <button
              onClick={() => setPreviewOpen(false)}
              data-testid="preview-close"
              className="w-9 h-9 flex items-center justify-center border border-border hover:bg-secondary rounded-sm"
              aria-label="Fermer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="w-full aspect-[16/10] bg-black">
            {book?.flipbook_preview_url && (
              <iframe
                title="Feuilleter un extrait"
                src={book.flipbook_preview_url}
                className="w-full h-full"
                allowFullScreen
              />
            )}
          </div>
          <div className="px-5 py-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground text-center sm:text-left">
              Aimez-vous ce que vous lisez ? Achetez pour découvrir le livre complet.
            </p>
            <Button onClick={buyNow} data-testid="preview-buy" className="rounded-sm">
              <ShoppingBag className="w-4 h-4 mr-2" /> Acheter maintenant
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
